import useApiCaller from 'hooks/apiCaller';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchCourseDetails } from 'reducer/courseDetailsSlice';
import { fetchCourses } from 'reducer/userCourseDetailSlice';
import useUserDetails from 'hooks/useUserDetails';
import { COMPANY_ROLES } from 'Constants/roles';
import useCredentials from '../../hooks/useCredentials';

// import subtitles from './subtitles.vtt';

const extempted = [20005, 50207];
const CourseDetails = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const apiCaller = useApiCaller();
  const courseId = params.id;
  const status = params.status;
  const { companyId } = useUserDetails();
  const uniqueCourseKeyInEmployeeCourses = params.uniqueCourseKeyInEmployeeCourses;
  const courseDetail = useSelector((state) => state?.detailsCourse?.data);
  const coursesData = useSelector((state) => state?.userCourseDetail?.coursesData);
  const [courseProgress, setCourseProgress] = useState(false);
  const videoRef = useRef(null);
  const docRef = useRef(null);
  const lastTimeRef = useRef(0);
  const [lastSentProgress, setLastSentProgress] = useState(0);
  const [lastTime, setLastTime] = useState(0);
  const [showResumeModal, setShowResumeModal] = useState(false);
  const [resumeFromTime, setResumeFromTime] = useState(0);
  const [hasMoreAttempts, setHasMoreAttempts] = useState(false);
  const [attemptLoading, setAttemptLoading] = useState(true);
  const [isVideoComplete, setIsVideoComplete] = useState(false);
  const [subtitleUrl, setSubtitleUrl] = useState(null);
  const { userData } = useCredentials();

  const [availableSubtitles, setAvailableSubtitles] = useState([]);
  const [selectedSubtitleLang, setSelectedSubtitleLang] = useState('en');

  // Multi-language video support
  const [availableVideos, setAvailableVideos] = useState([]);
  const [selectedVideoLang, setSelectedVideoLang] = useState('en');
  const [currentVideoUrl, setCurrentVideoUrl] = useState('');

  // Language selection modal
  const [showLangModal, setShowLangModal] = useState(false);

  const { percentCompletion, attempts } =
    coursesData.find(
      (val) =>
        val._id === courseId &&
        val.uniqueCourseKeyInEmployeeCourses === uniqueCourseKeyInEmployeeCourses
    ) || {};

  const isCourseCompleted = () => {
    const course = coursesData.find(
      (val) => val.uniqueCourseKeyInEmployeeCourses === uniqueCourseKeyInEmployeeCourses
    );
    return course?.courseProgress === 'VIEW COURSE';
  };

  const isAttemptDisabled = () => {
    if (extempted.includes(userData?.employeeId)) return false;
    if (attemptLoading) return true;
    const isVideoIncomplete = courseDetail.courseType === 'video' && !isVideoComplete;
    if (companyId === COMPANY_ROLES.SATIN_FINSERV_LTD) {
      return !hasMoreAttempts || isVideoIncomplete;
    }
    return isCourseCompleted() || !hasMoreAttempts || isVideoIncomplete;
  };

  const videoProgressKey = `videoProgress_${courseId}_${uniqueCourseKeyInEmployeeCourses}`;

  const formatVideoTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const checkResumePosition = () => {
    const saved = localStorage.getItem(videoProgressKey);
    if (saved && parseFloat(saved) > 10) {
      setResumeFromTime(parseFloat(saved));
      setShowResumeModal(true);
    }
  };

  const handleResumeVideo = () => {
    setShowResumeModal(false);
    if (videoRef.current && resumeFromTime > 0) {
      lastTimeRef.current = resumeFromTime;
      setLastTime(resumeFromTime);
      videoRef.current.currentTime = resumeFromTime;
    }
  };

  const handleStartOver = () => {
    setShowResumeModal(false);
    localStorage.removeItem(videoProgressKey);
    lastTimeRef.current = 0;
    setLastTime(0);
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  };

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const currentTime = videoRef.current.currentTime;
      const duration = videoRef.current.duration;

      if (currentTime > lastTimeRef.current + 2) {
        videoRef.current.currentTime = lastTimeRef.current;
      } else {
        lastTimeRef.current = currentTime;
        setLastTime(currentTime);
        if (currentTime > 0) {
          localStorage.setItem(videoProgressKey, currentTime.toString());
        }
      }

      const progressPercent = Math.floor((currentTime / duration) * 100);

      // if (
      //   progressPercent >= lastSentProgress + 5 &&
      //   progressPercent <= 100 &&
      //   percentCompletion < 100
      // ) {
      //   const data = { courseId, progress: progressPercent };

      //   apiCaller
      //     .post('/user/update-course-progress-beacon', data)
      //     .then(() => {
      //       setLastSentProgress(progressPercent);
      //       if (progressPercent === 100) {
      //         setCourseProgress(true);
      //       }
      //     })
      //     .catch((error) => {
      //       console.error('Error sending course progress:', error);
      //     });
      // }
    }
  }, [videoProgressKey]);

  const handleOnClickAttempts = () => {
    navigate(`/questionnaire/${courseId}/${uniqueCourseKeyInEmployeeCourses}`);
  };

  const handleScroll = useCallback(() => {
    if (docRef.current && !courseProgress) {
      const scrollTop = docRef.current.scrollTop;
      const scrollHeight = docRef.current.scrollHeight;
      const clientHeight = docRef.current.clientHeight;
      const scrollPercentage = ((scrollTop + clientHeight) / scrollHeight) * 100;

      if (scrollPercentage >= 100) {
        const data = { courseId, progress: 100 };

        apiCaller
          .post('/user/update-course-progress-beacon', data)
          .then(() => {
            setCourseProgress(true);
          })
          .catch((error) => {
            console.error('Error sending course progress:', error);
          });
      }
    }
  }, [courseProgress, courseId, apiCaller]);

  useEffect(() => {
    dispatch(fetchCourses({ apiCaller, status, uniqueCourseKeyInEmployeeCourses }));
    dispatch(fetchCourseDetails({ apiCaller, courseId, uniqueCourseKeyInEmployeeCourses }));
  }, [courseId, status, uniqueCourseKeyInEmployeeCourses, apiCaller, dispatch]);

  const fetchAttempt = useCallback(async () => {
    try {
      const response = await apiCaller.post('/user/getAttempt', {
        courseId,
        uniqueCourseKeyInEmployeeCourses,
      });
      const { attemptLength, maxAttempt } = response?.data?.data || {};
      setHasMoreAttempts(attemptLength < maxAttempt);
    } catch (err) {
      setHasMoreAttempts(false);
    } finally {
      setAttemptLoading(false);
    }
  }, [apiCaller, courseId, uniqueCourseKeyInEmployeeCourses]);

  useEffect(() => {
    fetchAttempt();
  }, [fetchAttempt]);

  useEffect(() => {
    const subtitles = courseDetail.availableSubtitles || [];
    setAvailableSubtitles(subtitles);

    // Set available videos and select default video
    if (courseDetail.availableVideos && courseDetail.availableVideos.length > 0) {
      setAvailableVideos(courseDetail.availableVideos);
      const defaultVideo = courseDetail.availableVideos.find((v) => v.isDefault);
      if (defaultVideo) {
        setSelectedVideoLang(defaultVideo.code);
        setCurrentVideoUrl(defaultVideo.url);
      }
      // Show language selection modal for video courses with multiple languages
      // Resume will be checked after language selection
      if (courseDetail.courseType === 'video') {
        setShowLangModal(true);
      }
    } else if (courseDetail.courseUrl) {
      // Legacy single video
      setCurrentVideoUrl(courseDetail.courseUrl);
      if (courseDetail.courseType === 'video' && subtitles.length > 1) {
        // Show language modal first; resume will be checked after selection
        setShowLangModal(true);
      } else if (courseDetail.courseType === 'video') {
        // Single language — check for saved resume position directly
        checkResumePosition();
      }
    }
  }, [courseDetail]);

  useEffect(() => {
    if (courseDetail?.courseType === 'video' && courseId) {
      // Revoke previous blob URL before creating a new one
      setSubtitleUrl(null);

      apiCaller
        .get(`${process.env.REACT_APP_BASE_URL}/user/getSubtitleFile`, {
          params: { courseId: courseId, lang: selectedSubtitleLang },
          responseType: 'blob',
        })
        .then(async (response) => {
          // Re-create blob with explicit text/vtt MIME type so all browsers
          // accept it as a valid WebVTT source regardless of what the server sent
          const text = await response.data.text();
          const blob = new Blob([text], { type: 'text/vtt' });
          const url = URL.createObjectURL(blob);
          setSubtitleUrl(url);
        })
        .catch((error) => {
          console.error('Error fetching subtitles:', error);
        });
    }
  }, [courseId, courseDetail?.courseType, selectedSubtitleLang, availableSubtitles]);

  useEffect(() => {
    return () => {
      if (subtitleUrl) {
        URL.revokeObjectURL(subtitleUrl);
      }
    };
  }, [subtitleUrl]);

  // Block forward seeking
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleSeeking = () => {
      if (video.currentTime > lastTimeRef.current + 2) {
        video.currentTime = lastTimeRef.current;
      }
    };

    video.addEventListener('seeking', handleSeeking);
    return () => {
      video.removeEventListener('seeking', handleSeeking);
    };
  }, [courseDetail.courseType, currentVideoUrl]);

  // Pause video when tab is not visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    if (percentCompletion === 100) {
      setCourseProgress(true);
      setIsVideoComplete(true);
      return;
    }

    const videoUpdateIntervalId = setInterval(() => {
      if (videoRef.current) {
        handleTimeUpdate();
      }
    }, 5000);

    const docElement = docRef.current;
    if (docElement) {
      docElement.addEventListener('scroll', handleScroll);
    }

    return () => {
      clearInterval(videoUpdateIntervalId);
      if (docElement) {
        docElement.removeEventListener('scroll', handleScroll);
      }
    };
  }, [percentCompletion, handleScroll, handleTimeUpdate]);

  // PDF Security: Global event listeners for PDF content
  useEffect(() => {
    if (courseDetail.courseType === 'pdf') {
      const preventDefault = (e) => {
        e.preventDefault();
        e.stopPropagation();
        return false;
      };

      // Prevent right-click
      const handleContextMenu = (e) => preventDefault(e);

      // Prevent keyboard shortcuts
      const handleKeyDown = (e) => {
        // Prevent Ctrl+S, Ctrl+P, F12, Ctrl+Shift+I, Ctrl+U
        if (
          (e.ctrlKey && (e.key === 's' || e.key === 'p' || e.key === 'u')) ||
          e.key === 'F12' ||
          (e.ctrlKey && e.shiftKey && e.key === 'I')
        ) {
          preventDefault(e);
        }
      };

      // Prevent drag and drop
      const handleDragStart = (e) => preventDefault(e);

      // Prevent text selection
      const handleSelectStart = (e) => preventDefault(e);

      document.addEventListener('contextmenu', handleContextMenu);
      document.addEventListener('keydown', handleKeyDown);
      document.addEventListener('dragstart', handleDragStart);
      document.addEventListener('selectstart', handleSelectStart);

      return () => {
        document.removeEventListener('contextmenu', handleContextMenu);
        document.removeEventListener('keydown', handleKeyDown);
        document.removeEventListener('dragstart', handleDragStart);
        document.removeEventListener('selectstart', handleSelectStart);
      };
    }
  }, [courseDetail.courseType]);

  const selectedSubtitleColor =
    availableSubtitles.find((s) => s.code === selectedSubtitleLang)?.color || '#ffffff';

  const handleSelectLanguage = (langCode) => {
    const video = availableVideos.find((v) => v.code === langCode);
    if (video) {
      setSelectedVideoLang(langCode);
      setCurrentVideoUrl(video.url);
      setIsVideoComplete(false);
      lastTimeRef.current = 0;
      setLastTime(0);
    }
    setSelectedSubtitleLang(langCode);
    setShowLangModal(false);
    // After language selection, check for saved resume position
    checkResumePosition();
  };

  // Language options shown in modal: union of video and subtitle languages
  const modalLanguageOptions = availableVideos.length > 0 ? availableVideos : availableSubtitles;

  return (
    <Layout>
      <style>
        {`
          /* PDF Security Styles */
          .pdf-container {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            -webkit-touch-callout: none !important;
            -webkit-tap-highlight-color: transparent !important;
          }
          
          .pdf-container iframe {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
            pointer-events: auto !important;
          }
          
          /* Disable text selection globally for PDF */
          .pdf-container * {
            -webkit-user-select: none !important;
            -moz-user-select: none !important;
            -ms-user-select: none !important;
            user-select: none !important;
          }
          
          /* Hide download buttons and controls */
          .pdf-container iframe::-webkit-media-controls {
            display: none !important;
          }
          
          /* Additional security for PDF viewer */
          .pdf-container iframe {
            -webkit-filter: none !important;
            filter: none !important;
          }
        `}
      </style>
      <Box className="p-3" sx={{ marginLeft: 2 }}>
        <Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <div
                ref={docRef}
                style={{
                  height: courseDetail?.courseType === 'pdf' ? 'calc(100vh - 300px)' : 'auto',
                  minHeight: courseDetail?.courseType === 'pdf' ? '600px' : 'auto',
                  borderRadius: 5,
                  overflow: 'auto',
                }}
                className="file-responsive"
              >
                {courseDetail.courseType === 'pdf' && (
                  <div
                    className="pdf-container"
                    style={{
                      position: 'relative',
                      width: '100%',
                      height: '100%',
                      minHeight: '600px',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onDragStart={(e) => e.preventDefault()}
                    onSelectStart={(e) => e.preventDefault()}
                    onCopy={(e) => e.preventDefault()}
                    onCut={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                  >
                    <iframe
                      width="100%"
                      height="100%"
                      src={`${courseDetail.courseUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&scrollbar=0&view=FitH`}
                      title="Embedded PDF"
                      style={{
                        border: 'none',
                        pointerEvents: 'auto',
                        minHeight: '600px',
                      }}
                      onLoad={(e) => {
                        // Additional security: try to disable context menu in iframe
                        try {
                          const iframeDoc =
                            e.target.contentDocument || e.target.contentWindow.document;
                          if (iframeDoc) {
                            iframeDoc.addEventListener('contextmenu', (evt) =>
                              evt.preventDefault()
                            );
                            iframeDoc.addEventListener('selectstart', (evt) =>
                              evt.preventDefault()
                            );
                            iframeDoc.addEventListener('dragstart', (evt) => evt.preventDefault());
                          }
                        } catch (error) {
                          // Cross-origin restrictions may prevent this
                          console.log('PDF iframe security: Cross-origin restrictions apply');
                        }
                      }}
                    />
                    <style>
                      {`
                        iframe {
                          -webkit-user-select: none !important;
                          -moz-user-select: none !important;
                          -ms-user-select: none !important;
                          user-select: none !important;
                        }
                      `}
                    </style>
                  </div>
                )}

                {courseDetail.courseType === 'pptx' && (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                      courseDetail.courseUrl
                    )}`}
                    title="Embedded PowerPoint"
                  ></iframe>
                )}
                {console.log('currentVideoUrl', currentVideoUrl)}
                {courseDetail.courseType === 'video' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                      <video
                        ref={videoRef}
                        key={currentVideoUrl}
                        width="100%"
                        height="100%"
                        controls
                        controlsList="nodownload noremoteplayback noplaybackrate"
                        disablePictureInPicture
                        disableRemotePlayback
                        src={currentVideoUrl}
                        title="Embedded Video"
                        onTimeUpdate={handleTimeUpdate}
                        onEnded={() => {
                          setIsVideoComplete(true);
                          localStorage.removeItem(videoProgressKey);
                        }}
                        onPlay={() => {
                          const t = videoRef.current.currentTime;
                          lastTimeRef.current = t;
                          setLastTime(t);
                        }}
                        style={{
                          '--webkit-media-controls-download-button': 'none',
                          '--webkit-media-controls-playback-rate-button': 'none',
                          '--webkit-media-controls-picture-in-picture-button': 'none',
                        }}
                      >
                        {subtitleUrl && (
                          <track
                            key={subtitleUrl}
                            label="Subtitles"
                            kind="subtitles"
                            srcLang={selectedSubtitleLang}
                            src={subtitleUrl}
                            default
                            onLoad={(e) => {
                              // 'default' only auto-activates on initial parse.
                              // When src changes dynamically the track stays
                              // disabled, so we force it to 'showing' here.
                              if (e.target && e.target.track) {
                                e.target.track.mode = 'showing';
                              }
                            }}
                          />
                        )}
                        Your browser does not support the video tag.
                      </video>
                      <style>
                        {`
                          video::-webkit-media-controls-download-button {
                            display: none !important;
                          }
                          video::-webkit-media-controls-playback-rate-button {
                            display: none !important;
                          }
                          video::-webkit-media-controls-picture-in-picture-button {
                            display: none !important;
                          }
                          video::-webkit-media-controls-remote-playback-button {
                            display: none !important;
                          }
                          /* Subtitle styling */
                          ::cue {
                            font-family: 'monospace' !important;
                            font-size: 0.8em;
                            line-height: 1.2;
                            background-color: rgba(0,0,0,0.7);
                            color: ${selectedSubtitleColor};
                          }
                        `}
                      </style>
                    </div>

                    {/* Video Language Selector - Only show if multiple videos available */}
                    {availableVideos.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          marginTop: '15px',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 700,
                            fontSize: '16px',
                            marginRight: '8px',
                          }}
                        >
                          Video Language:
                        </div>
                        {availableVideos.map((video) => (
                          <button
                            key={video.code}
                            onClick={() => {
                              setSelectedVideoLang(video.code);
                              setCurrentVideoUrl(video.url);
                              // Reset video completion status when switching videos
                              setIsVideoComplete(false);
                              setLastTime(0);
                            }}
                            style={{
                              padding: '6px 14px',
                              borderRadius: '6px',
                              border:
                                selectedVideoLang === video.code
                                  ? '2px solid #2800B1'
                                  : '1px solid #ccc',
                              background: selectedVideoLang === video.code ? '#e7f1ff' : 'white',
                              cursor: 'pointer',
                              fontWeight: selectedVideoLang === video.code ? 'bold' : 'normal',
                              transition: '0.2s',
                            }}
                          >
                            {video.name.toUpperCase()}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Subtitle Language Selector */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '10px',
                        marginTop: '15px',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 700,
                          fontSize: '16px',
                          marginRight: '8px',
                        }}
                      >
                        Subtitle Language:
                      </div>
                      {availableSubtitles.map((sub) => (
                        <button
                          key={sub.code}
                          onClick={() => setSelectedSubtitleLang(sub.code)}
                          style={{
                            padding: '6px 14px',
                            borderRadius: '6px',
                            border:
                              selectedSubtitleLang === sub.code
                                ? '2px solid #007bff'
                                : '1px solid #ccc',
                            background: selectedSubtitleLang === sub.code ? '#e7f1ff' : 'white',
                            cursor: 'pointer',
                            fontWeight: selectedSubtitleLang === sub.code ? 'bold' : 'normal',
                            transition: '0.2s',
                          }}
                        >
                          {sub.name.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {courseDetail.courseType === 'docx' && (
                  <iframe
                    width="100%"
                    height="100%"
                    src={`https://docs.google.com/viewer?url=${encodeURIComponent(
                      courseDetail.courseUrl
                    )}&embedded=true`}
                    title="Embedded DOCX"
                    style={{ border: 'none' }}
                  ></iframe>
                )}
              </div>
            </Grid>
          </Grid>
        </Box>

        <Grid container spacing={2} className="mt-3">
          <Grid item xs={9}>
            <Box>
              <Box sx={{ color: '#110B27', fontSize: 20, fontWeight: 600 }}>Description</Box>
              <Box
                className="mt-2"
                sx={{ fontSize: 16, color: '#110B2C', fontWeight: 400, textAlign: 'justify' }}
              >
                {courseDetail?.description}
              </Box>
            </Box>
          </Grid>
          <Grid
            item
            xs={3}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Button
              variant="contained"
              size="medium"
              //disabled={isAttemptDisabled()}
              sx={{
                borderRadius: 50,
                background: '#2800B1',
                fontSize: 12,
                border: '1px solid #2800B1',
              }}
              onClick={handleOnClickAttempts}
            >
              Attempt Test
            </Button>
          </Grid>
        </Grid>
      </Box>
      {/* Language Selection Modal */}
      <Dialog open={showLangModal && courseDetail.courseType === 'video'} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>
          Select Video Language
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
            Choose the language you'd like to watch this video in. Subtitles will match your
            selection.
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            {modalLanguageOptions.map((lang) => (
              <Button
                key={lang.code}
                variant="outlined"
                onClick={() => handleSelectLanguage(lang.code)}
                sx={{
                  borderRadius: '8px',
                  border: '1px solid #e2e8f0',
                  color: '#1e293b',
                  fontWeight: 600,
                  px: 3,
                  py: 1.5,
                  textTransform: 'none',
                  '&:hover': {
                    background: '#3b82f6',
                    color: '#fff',
                    borderColor: '#3b82f6',
                  },
                }}
              >
                {lang.name}
              </Button>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="text"
            onClick={() => setShowLangModal(false)}
            sx={{ color: '#64748b', textTransform: 'none' }}
          >
            Continue with default (English)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Video Resume Modal */}
      <Dialog open={showResumeModal} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontSize: 20, pb: 1 }}>Resume Video</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: '#64748b' }}>
            You previously watched up to <strong>{formatVideoTime(resumeFromTime)}</strong>. Would
            you like to continue from where you left off?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            variant="text"
            onClick={handleStartOver}
            sx={{ color: '#64748b', textTransform: 'none' }}
          >
            Start Over
          </Button>
          <Button
            variant="contained"
            onClick={handleResumeVideo}
            sx={{
              background: '#2800B1',
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { background: '#1a0080' },
            }}
          >
            Resume
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default CourseDetails;
